import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { fetchPerplexitySignals } from '../../../redux/HSignalSlice/hsignalSlice';
import { fetchContactsForCompanies, fetchContactCompanyIndex } from '../../../redux/ContactSlice/contactSlice';
import { fetchUserJobs } from '../../../redux/JobSlice/jobSlice';
import { deleteSavedJob, fetchSavedJobs } from '../../../redux/SavedJobSlice/savedJobSlice';
import { deleteSavedCompany, fetchSavedCompanies } from '../../../redux/SavedCompanySlice/savedCompanySlice';
import { fetchCompanies } from '../../../redux/CompanySlice/companySlice';
import { fetchLinkedinDoneContacts, fetchFirstEmailDoneContacts, fetchDMDoneContacts, fetchFollowupEmailDoneContacts, fetchStageProgress } from '../../../redux/DailyTasksSlice/dailyTaskSlice';

import Navbaar from '../../../components/Navbar/Navbaar';
import NetPipelineRow, { deriveProgress } from '../../../components/NetworkingPage/NetContent/NetPipelineRow';
import NetSequenceFlow from '../../../components/NetworkingPage/NetContent/NetSequenceFlow';
import Sidebar from '../../../components/Navbar/SideNavbar';
import { ArrowLeft } from 'lucide-react';

import NetNotFound from "../../../assets/images/Networking_Page/NetNotFound.png";
import NetNotFoundBig from "../../../assets/images/Networking_Page/NetNotFoundBig.png";
import { checkLinkedInConnectionStatus, linkedInLogin, fetchMyInviteStatuses } from '../../../redux/LIAuthSlice/liAuthSlice';

import SearchIcon from '@mui/icons-material/Search';
import SubscriptionModal from '../../../components/Modals/SubscriptionModal';
import { useLocation } from 'react-router-dom';
import { useNavbarActions } from '../../../context/NavbarActionsContext';

// Maps human-readable departments -> Apollo tags
const departmentValueMap = {
    'C-Suite / Executive': 'c_suite',
    'Product': 'product_management',
    'Engineering / Technical': 'master_engineering_technical',
    'IT & Security': 'master_information_technology',
    'Design': 'design',
    'Education': 'education',
    'Finance': 'master_finance',
    'Human Resources': 'master_human_resources',
    'Legal': 'master_legal',
    'Marketing': 'master_marketing',
    'Medical & Health': 'medical_health',
    'Operations': 'master_operations',
    'Sales': 'master_sales',
    'Consulting': 'consulting',
};

// Helper function to clean and format job titles
const cleanJobTitle = (jobTitle) => {
    if (!jobTitle) return "";
    
    // If title is too long, find a natural breakpoint
    if (jobTitle.length > 50) {
        // Look for common separators like commas, hyphens, or "at"
        const commaIndex = jobTitle.indexOf(',');
        const dashIndex = jobTitle.indexOf(' - ');
        const atIndex = jobTitle.toLowerCase().indexOf(' at ');
        
        // Use the first separator that appears in the string
        let cutoffIndex = -1;
        if (commaIndex > 0 && (cutoffIndex === -1 || commaIndex < cutoffIndex)) {
            cutoffIndex = commaIndex;
        }
        if (dashIndex > 0 && (cutoffIndex === -1 || dashIndex < cutoffIndex)) {
            cutoffIndex = dashIndex;
        }
        if (atIndex > 0 && (cutoffIndex === -1 || atIndex < cutoffIndex)) {
            cutoffIndex = atIndex;
        }
        
        // If we found a separator, truncate at that point
        if (cutoffIndex > 0) {
            return jobTitle.substring(0, cutoffIndex);
        }
        
        // If no separator is found, simply truncate with ellipsis
        return jobTitle.substring(0, 47) + "...";
    }
    
    // Remove duplicate phrases (e.g., "US Federal Specialist Solution Architect Team, US Federal Specialist Solution Architect Team")
    const parts = jobTitle.split(', ');
    if (parts.length > 1) {
        const uniqueParts = [...new Set(parts)];
        return uniqueParts.join(', ');
    }
    
    return jobTitle;
};

const getObjectId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value._id || value.id || '';
};

const getSavedCompanyOrgId = (savedCompany) => (
    savedCompany?.organizationId ||
    savedCompany?.companyId?.organizationId ||
    getObjectId(savedCompany?.companyId)
);

const getSavedJobOrgId = (savedJob) => (
    savedJob?.organizationId ||
    savedJob?.jobId?.organizationId ||
    savedJob?.jobId?.companyId?.organizationId ||
    getObjectId(savedJob?.jobId?.companyId)
);

// Normalized company name — the reliable join key across saved jobs, saved
// companies, hiring signals and the jobs feed (contacts are keyed by name).
const normName = (name) => String(name || '').trim().toLowerCase();

// Does a company (by name and/or org id) have any stored contacts, per the
// lightweight index? Mirrors the backend's substring name match.
const companyHasContactsInIndex = (name, orgId, index) => {
    if (!index) return false;
    const nm = normName(name);
    const orgIds = index.orgIds || [];
    const names = index.names || [];
    if (orgId && orgIds.includes(String(orgId))) return true;
    if (nm.length >= 2) {
        // Backend matches contacts whose organization_name CONTAINS the company name.
        return names.some(idxName => idxName.includes(nm));
    }
    return false;
};

function SkeletonRow() {
    return (
        <div className="bg-white rounded-2xl border border-[#E6E8EE] px-5 py-4 flex items-center gap-4 animate-pulse">
            <div className="w-12 h-12 rounded-xl bg-[#F1F5F9] flex-shrink-0" />
            <div className="flex-1 min-w-0 space-y-2.5">
                <div className="h-3.5 bg-[#F1F5F9] rounded-full w-36" />
                <div className="h-3 bg-[#F1F5F9] rounded-full w-52" />
                <div className="h-2.5 bg-[#F1F5F9] rounded-full w-28" />
            </div>
            <div className="hidden md:flex items-center gap-1 flex-shrink-0">
                {[0, 1, 2].map(i => (
                    <div key={i} className="w-7 h-7 rounded-full bg-[#F1F5F9]" style={{ marginLeft: i ? -8 : 0 }} />
                ))}
            </div>
            <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                <div className="h-3 bg-[#F1F5F9] rounded-full w-20" />
                <div className="h-8 bg-[#F1F5F9] rounded-xl w-20" />
            </div>
        </div>
    );
}

const Networking = () => {
    const dispatch = useDispatch();
    const location = useLocation();

    const newOrgIds = location.state?.newOrgIds || [];
    const newlyAddedOrgIds = useMemo(() => new Set(newOrgIds), [newOrgIds]);

    const { perplexitySignals, loading: signalsLoading } = useSelector(state => state.hsignal);
    const { contactsByCompanyId, isSearching, contactCompanyIndex, contactCompanyIndexLoaded } = useSelector(state => state.contacts);
    const feedJobs = useSelector(state => state.jobs.enrichedJobsArray);
    const feedJobsLoading = useSelector(state => state.jobs.loading);
    const { companies } = useSelector((state) => state.companies);
    const savedCompanies = useSelector(state => state.savedCompanies.savedCompanies);
    const savedCompaniesLoading = useSelector(state => state.savedCompanies.loading);
    const savedJobs = useSelector(state => state.savedJobs.savedJobs);
    const savedJobsLoading = useSelector(state => state.savedJobs.savedJobsLoading);
    const user = useSelector(state => state.auth.user);
    const { linkedinDone, firstEmailDone, dmDone, followupEmailDone, stageProgressByCompany } = useSelector((state) => state.dailyTasks);
    const { isLoading: liConnectLoading, isLinkedInConnected, connectionChecked } = useSelector((state) => state.liauth);

    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [isSubscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("")
    const [sortBy, setSortBy] = useState('date'); // 'date' | 'alpha' | 'progress'
    const [activeFilter, setActiveFilter] = useState('all'); // all | in_progress | not_started | completed
    const [sourceFilter, setSourceFilter] = useState(null); // null | 'job' | 'signal' | 'saved' | 'unsaved'
    const [flowCompany, setFlowCompany] = useState(null); // when set → show full-page sequence flow
    const [pageSize, setPageSize] = useState(20);     // companies per page
    const [currentPage, setCurrentPage] = useState(1); // 1-based page index

    // Check if onboarding is active
    const isOnboardingActive = typeof window !== 'undefined' && (
        !localStorage.getItem('hasCompletedOnboarding') || 
        new URLSearchParams(window.location.search).get('onboarding') === 'true'
    );

    useEffect(() => {
        // Skip only unauthenticated onboarding/demo sessions. Authenticated users
        // should still see saved networking data if the local onboarding flag is stale.
        if (isOnboardingActive && !user?._id) {
            return;
        }
        // Only fetch when the list isn't already in Redux — previously this
        // refetched ALL companies every time savedJobs/savedCompanies resolved.
        if ((companies?.length || 0) === 0) dispatch(fetchCompanies());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isOnboardingActive, user?._id]);

    useEffect(() => {
        // Skip only unauthenticated onboarding/demo sessions. Authenticated users
        // should still load saved companies/jobs if the local onboarding flag is stale.
        if (isOnboardingActive && !user?._id) {
            return;
        }
        if (user?._id) {
            // Only fetch data that isn't already cached in Redux, so navigating
            // back to this page shows the cached pipeline instantly (no loading
            // flash). A hard refresh clears Redux and re-fetches everything.
            if ((perplexitySignals?.length || 0) === 0) dispatch(fetchPerplexitySignals());
            if ((savedCompanies?.length || 0) === 0) dispatch(fetchSavedCompanies(user._id));
            if ((savedJobs?.length || 0) === 0) dispatch(fetchSavedJobs(user._id));
            if ((feedJobs?.length || 0) === 0) dispatch(fetchUserJobs());
            if (!contactCompanyIndexLoaded) dispatch(fetchContactCompanyIndex());
            dispatch(fetchMyInviteStatuses());
            dispatch(fetchLinkedinDoneContacts(user._id));
            dispatch(fetchFirstEmailDoneContacts(user._id));
            dispatch(fetchDMDoneContacts(user._id));
            dispatch(fetchFollowupEmailDoneContacts(user._id));
            dispatch(fetchStageProgress());
            dispatch(checkLinkedInConnectionStatus());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, user?._id, isOnboardingActive]);

    useEffect(() => {
        if (!toast.show) return;

        const timer = setTimeout(() => {
            setToast({ show: false, message: '', type: 'success' });
        }, 3000); // 3000 milliseconds = 3 seconds

        return () => clearTimeout(timer);

    }, [toast]);

    useEffect(() => {
        if (location.state?.toastMessage) {
            setToast({
                show: true,
                message: location.state.toastMessage,
                type: location.state.toastType || 'success'
            });

            const timer = setTimeout(() => {
                setToast({ show: false, message: '', type: 'success' });
            }, 5000); // Hide after 5 seconds

            return () => clearTimeout(timer);
        }
    }, [location.state]);

    // Unified, de-duplicated company list built from ALL four sources, keyed by
    // normalized company name (the reliable join key — contacts match by name).
    // Sources: saved jobs, saved companies (saved signals), the hiring-signal
    // feed (saved + unsaved), and the jobs feed (unsaved jobs). Only companies
    // that actually have stored contacts (per the index) are kept.
    const pipelineEntries = useMemo(() => {
        const byName = new Map(); // normName -> entry

        const ensure = (name) => {
            const key = normName(name);
            if (!key) return null;
            if (!byName.has(key)) {
                byName.set(key, {
                    nameKey: key,
                    companyName: name,
                    companyLogo: '',
                    hqLocation: '',
                    jobPosition: '',
                    orgId: '',        // best available org id (for contact fetch + index match)
                    company: '',      // Companies _id (for buildCompanyData)
                    savedJobId: null,
                    savedCompanyId: null,
                    hasJob: false,
                    hasSignal: false,
                    savedJob: false,
                    savedSignal: false,
                    dateAdded: null,
                });
            }
            return byName.get(key);
        };

        const bumpDate = (entry, d) => {
            if (!d) return;
            if (!entry.dateAdded || new Date(d).getTime() > new Date(entry.dateAdded).getTime()) {
                entry.dateAdded = d;
            }
        };

        // 1. Saved jobs
        savedJobs.forEach(job => {
            const e = ensure(job?.jobId?.companyId?.name || job?.jobId?.companyName);
            if (!e) return;
            e.hasJob = true; e.savedJob = true;
            e.savedJobId = e.savedJobId || job._id;
            e.orgId = e.orgId || getSavedJobOrgId(job);
            e.company = e.company || getObjectId(job?.jobId?.companyId);
            e.companyLogo = e.companyLogo || job?.jobId?.companyLogo || job?.jobId?.companyId?.companyLogo || '';
            e.hqLocation = e.hqLocation || job?.jobId?.companyId?.hqLocation || '';
            e.jobPosition = e.jobPosition || cleanJobTitle(job?.jobId?.jobPosition);
            bumpDate(e, job.savedAt);
        });

        // 2. Saved companies (explicitly saved hiring signals)
        savedCompanies.forEach(c => {
            const e = ensure(c?.companyId?.name);
            if (!e) return;
            e.hasSignal = true; e.savedSignal = true;
            e.savedCompanyId = e.savedCompanyId || c._id;
            e.orgId = e.orgId || getSavedCompanyOrgId(c);
            e.company = e.company || getObjectId(c?.companyId);
            e.companyLogo = e.companyLogo || c?.companyId?.companyLogo || '';
            e.hqLocation = e.hqLocation || c?.companyId?.hqLocation || '';
            bumpDate(e, c.savedAt);
        });

        // 3. Hiring-signal feed (saved + unsaved)
        (perplexitySignals || []).forEach(s => {
            const e = ensure(s?.companyName || s?.company?.name);
            if (!e) return;
            e.hasSignal = true;
            e.orgId = e.orgId || s.organizationId || getObjectId(s.company);
            e.company = e.company || getObjectId(s.company);
            e.companyLogo = e.companyLogo || s?.companyLogo || s?.company?.companyLogo || '';
            e.hqLocation = e.hqLocation || s?.company?.hqLocation || '';
        });

        // 4. Jobs feed (unsaved jobs)
        (feedJobs || []).forEach(j => {
            const e = ensure(j?.companyName || j?.companyId?.name);
            if (!e) return;
            e.hasJob = true;
            e.orgId = e.orgId || j.organizationId || getObjectId(j.companyId);
            e.company = e.company || getObjectId(j.companyId);
            e.companyLogo = e.companyLogo || j?.companyLogo || j?.companyId?.companyLogo || '';
            e.hqLocation = e.hqLocation || j?.jobLocation || j?.companyId?.hqLocation || '';
            e.jobPosition = e.jobPosition || cleanJobTitle(j?.jobPosition);
        });

        // Keep companies that have stored contacts (per the index), PLUS all
        // user-curated hiring signals / saved items. The latter are always let
        // through so their contacts get fetched — otherwise a signal-only company
        // is dropped before we ever look up its contacts (and the display filter
        // still hides it later if no contacts are found).
        return Array.from(byName.values()).filter(e =>
            e.hasSignal || e.savedJob || e.savedSignal ||
            companyHasContactsInIndex(e.companyName, e.orgId, contactCompanyIndex)
        );
    }, [savedJobs, savedCompanies, perplexitySignals, feedJobs, contactCompanyIndex]);

    const [fetchedCompanyIds, setFetchedCompanyIds] = useState(new Set());
    // Contacts are now fetched lazily for only the *visible* (paginated) companies —
    // see the effect after `visibleCompanies` below — instead of all ~55 at once.

    const networkingCompanies = useMemo(() => {
        const preferredDepartmentTags = new Set(
            (user?.preferredDepartment || []).map(dep => {
                if (!dep) return null;
                if (
                    dep.startsWith("master_") ||
                    dep === "c_suite" ||
                    dep === "product_management" ||
                    dep === "design" ||
                    dep === "education" ||
                    dep === "medical_health" ||
                    dep === "consulting"
                ) {
                    return dep;
                }
                return departmentValueMap[dep] || dep;
            }).filter(Boolean)
        );

        const doneSets = {
            1: new Set(firstEmailDone || []),
            2: new Set(linkedinDone || []),
            3: new Set(dmDone || []),
            4: new Set(followupEmailDone || []),
        };

        return pipelineEntries.map(entry => {
            const key = entry.orgId || entry.companyName;

            const companyContactInfo = {
                recruiterContacts: [],
                otherContacts: [],
                loading: true,
                error: null,
                ...(contactsByCompanyId[key] || {}),
            };

            const displayData = {
                organizationId: entry.orgId || key,
                company: entry.company,
                companyName: entry.companyName || "Unknown Company",
                companyLogo: entry.companyLogo || "",
                hqLocation: entry.hqLocation || "",
                jobPosition: entry.jobPosition || "",
                dateAdded: entry.dateAdded,
            };

            const preferredContacts = companyContactInfo.otherContacts.filter(contact => {
                const contactDepartments = contact.departments || [];
                return contactDepartments.some(dep => preferredDepartmentTags.has(dep));
            });

            const relatedContacts = companyContactInfo.otherContacts.filter(contact => {
                const contactDepartments = contact.departments || [];
                return !contactDepartments.some(dep => preferredDepartmentTags.has(dep));
            });

            const currentCompanyStepContacts = {
                1: [...companyContactInfo.recruiterContacts, ...preferredContacts, ...relatedContacts],
                2: companyContactInfo.recruiterContacts,
                3: companyContactInfo.recruiterContacts,
                4: companyContactInfo.recruiterContacts,
            };

            // Stages the user explicitly completed in the sequence flow
            // ("Complete & go to Stage N"), persisted in the DB. Look up by the
            // company key AND by plain name (covers rows saved before the flow
            // knew the org id).
            const manualStages = new Set([
                ...(stageProgressByCompany[String(key)] || []),
                ...(stageProgressByCompany[String(entry.companyName || '')] || []),
            ].map(Number));

            // Count all 5 stages. Stage 5 (tailor resume & apply) has no contacts,
            // so it only completes via the manual "Mark complete" button.
            let completedStepsCount = 0;
            for (let step = 1; step <= 5; step++) {
                const contactsInStep = currentCompanyStepContacts[step] || [];
                const doneContactsForStep = doneSets[step];
                const isStepFullyCompleted = contactsInStep.length > 0 &&
                    contactsInStep.every(contact => doneContactsForStep.has(contact._id));
                if (isStepFullyCompleted || manualStages.has(step)) completedStepsCount++;
            }

            const source = entry.hasJob && entry.hasSignal ? 'both'
                : entry.hasJob ? 'job'
                : 'signal';

            return {
                organizationId: entry.orgId || key,
                savedJobId: entry.savedJobId,
                savedCompanyId: entry.savedCompanyId,
                savedJob: entry.savedJob,
                savedSignal: entry.savedSignal,
                source,
                signalData: displayData,
                recruiterContacts: companyContactInfo.recruiterContacts,
                preferredContacts,
                relatedContacts,
                allContacts: [...companyContactInfo.recruiterContacts, ...preferredContacts, ...relatedContacts],
                loading: companyContactInfo.loading,
                error: companyContactInfo.error,
                completedSteps: completedStepsCount,
            };
        });
    }, [pipelineEntries, contactsByCompanyId, user?.preferredDepartment, linkedinDone, firstEmailDone, dmDone, followupEmailDone, stageProgressByCompany]);

    // True while the initial saved-jobs / saved-companies / signals fetch is in flight.
    // During this window we show skeleton rows instead of partially-loaded data.
    const isPageLoading = savedJobsLoading || savedCompaniesLoading || signalsLoading || feedJobsLoading || !contactCompanyIndexLoaded;

    // Decide visibility WITHOUT requiring a full contacts fetch, so we can paginate and
    // lazy-load. Before a company's contacts are fetched, trust the lightweight contact
    // index / saved-signal status; after fetch, require real contacts.
    const companyHasContactsForDisplay = (company) => {
        const orgKey = company.organizationId || company.signalData?.companyName;
        const loaded = (company.allContacts || []).length > 0;
        const fetched = fetchedCompanyIds.has(orgKey);
        return loaded || (!fetched && (
            company.savedJob || company.savedSignal ||
            company.source === 'signal' || company.source === 'both' ||
            companyHasContactsInIndex(company.signalData?.companyName, company.organizationId, contactCompanyIndex)
        ));
    };

    const filterCompanies = (networkingCompanies) => {
        return networkingCompanies.filter((company) => {
            const matchesSearch = !searchQuery || company.signalData?.companyName?.toLowerCase().includes(searchQuery.toLowerCase());
            const hasContacts = companyHasContactsForDisplay(company);

            const isSaved = company.savedJob || company.savedSignal;
            let matchesSource = true;
            if (sourceFilter === 'job')     matchesSource = company.source === 'job' || company.source === 'both';
            if (sourceFilter === 'signal')  matchesSource = company.source === 'signal' || company.source === 'both';
            if (sourceFilter === 'saved')   matchesSource = isSaved;
            if (sourceFilter === 'unsaved') matchesSource = !isSaved;

            return matchesSearch && hasContacts && matchesSource;
        })
    }

    const filteredCompanies = filterCompanies(networkingCompanies);

    const sortedCompanies = useMemo(() => {
        const arr = [...filteredCompanies];

        if (sortBy === 'date') {
            // newest first
            arr.sort((a, b) => {
                const da = a.signalData?.dateAdded ? new Date(a.signalData.dateAdded).getTime() : 0;
                const db = b.signalData?.dateAdded ? new Date(b.signalData.dateAdded).getTime() : 0;
                return db - da;
            });
        } else if (sortBy === 'alpha') {
            arr.sort((a, b) => (a.signalData.companyName || '').localeCompare(b.signalData.companyName || ''));
        } else if (sortBy === 'progress') {
            arr.sort((a, b) => (b.completedSteps || 0) - (a.completedSteps || 0));
        }

        return arr;
    }, [filteredCompanies, sortBy]);

    const buildCompanyData = (company) => ({
        _id: company.signalData.company,
        // Stage progress is keyed on organizationId (name fallback) — must match
        // the pipeline list's company key or saved progress won't render there.
        organizationId: company.organizationId,
        name: company.signalData.companyName || "Unknown",
        companyLogo: company.signalData.companyLogo || "https://static.thenounproject.com/png/4149271-200.png",
        hqLocation: company.signalData.hqLocation || "Unknown",
        email: company.email || "N/A",
        description: company.description || "No description available",
        allContacts: company.allContacts,
        recruiterContacts: company.recruiterContacts,
        preferredContacts: company.preferredContacts,
        relatedContacts: company.relatedContacts,
        jobPosition: company.signalData.jobPosition || "", // Job position already cleaned
        completedSteps: company.completedSteps,
        source: company.source,           // 'job' | 'signal' | 'both'
        savedJob: company.savedJob,
        savedSignal: company.savedSignal,
    });

    // Open the full-page sequence flow for a company (replaces the right sidebar).
    // Push a history entry so the browser Back button closes the flow and returns
    // to the pipeline, instead of leaving /networking entirely.
    const handleOpenFlow = (company) => {
        setFlowCompany(buildCompanyData(company));
        window.history.pushState({ ccNetFlow: true }, '');
        window.scrollTo?.({ top: 0, behavior: 'smooth' });
    };

    // Close the flow. If our pushed history entry is current, go back to consume
    // it (fires popstate → clears flowCompany); otherwise just clear the state.
    const closeFlow = () => {
        if (window.history.state?.ccNetFlow) window.history.back();
        else setFlowCompany(null);
    };

    // Browser Back while the flow is open → close the flow (the pushed entry is
    // already popped by the browser here) rather than navigating away.
    useEffect(() => {
        const onPop = () => setFlowCompany(null);
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    // Filter counts for the pill chips — count companies eligible to show (via the index),
    // so the counts are stable regardless of which page's contacts have been fetched.
    const filterCounts = useMemo(() => {
        const withRecruiters = networkingCompanies.filter(companyHasContactsForDisplay);
        const counts = { all: withRecruiters.length, in_progress: 0, not_started: 0, completed: 0 };
        withRecruiters.forEach((c) => {
            counts[deriveProgress(c.completedSteps).status] += 1;
        });
        return counts;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [networkingCompanies, fetchedCompanyIds, contactCompanyIndex]);

    // Apply the active status filter on top of the search + sort pipeline.
    const pipelineCompanies = useMemo(() => {
        if (activeFilter === 'all') return sortedCompanies;
        return sortedCompanies.filter((c) => deriveProgress(c.completedSteps).status === activeFilter);
    }, [sortedCompanies, activeFilter]);

    // ── Pagination (numbered, page-size selectable) ──
    // Reset to the first page whenever the filters/search/sort/page-size change.
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, sortBy, activeFilter, sourceFilter, pageSize]);

    const totalCompanies = pipelineCompanies.length;
    const totalPages = Math.max(1, Math.ceil(totalCompanies / pageSize));

    // Clamp if the list shrinks below the current page.
    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    const visibleCompanies = useMemo(
        () => pipelineCompanies.slice((currentPage - 1) * pageSize, currentPage * pageSize),
        [pipelineCompanies, currentPage, pageSize]
    );
    const goToPage = (p) => setCurrentPage(Math.min(Math.max(1, p), totalPages));

    // Fetch full contacts ONLY for the companies on the current page (the visible slice).
    // Companies appear via the lightweight index first, then their contacts stream in;
    // "Load more" fetches the next page's contacts. `fetchedCompanyIds` caches across
    // navigations so the backend is never hit twice for the same company.
    useEffect(() => {
        const needed = [];
        visibleCompanies.forEach((company) => {
            const key = company.organizationId || company.signalData?.companyName;
            if (!key || newlyAddedOrgIds.has(key) || fetchedCompanyIds.has(key)) return;
            const existing = contactsByCompanyId[key];
            if (!existing || (existing.recruiterContacts?.length === 0 && existing.otherContacts?.length === 0)) {
                needed.push({ key, name: company.signalData?.companyName });
            }
        });
        if (needed.length > 0) {
            // One batched request for the whole visible page instead of one per company.
            dispatch(fetchContactsForCompanies({ companies: needed }));
            setFetchedCompanyIds((prev) => {
                const next = new Set(prev);
                needed.forEach(({ key }) => next.add(key));
                return next;
            });
        }
    }, [dispatch, visibleCompanies, contactsByCompanyId, fetchedCompanyIds, newlyAddedOrgIds]);

    // Kick off LinkedIn connect. The popup + success detection + status polling
    // are owned by Navbaar's global linkedInAuthUrl watcher, so this only fires
    // the login (which sets the auth URL). Opening a popup here too would produce
    // a duplicate window.
    const handleLIConnect = () => {
        if (!user?._id) {
            console.error("User ID is not available for LinkedIn login.");
            return;
        }
        dispatch(linkedInLogin());
    };

    const handleDeleteCompany = (savedJobId, savedCompanyId) => {
        if (savedJobId) {
            dispatch(deleteSavedJob(savedJobId));
        }
        if (savedCompanyId) {
            dispatch(deleteSavedCompany(savedCompanyId));
        }
        setToast({ show: true, message: 'Company removed from your list.', type: 'success' });
    };

    // Handle callback query params (success/error from LinkedIn auth)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const successParam = params.get('success');
        const errorParam = params.get('error');

        // If the LinkedIn callback landed the whole app inside the auth POPUP
        // (window.open names it 'LinkedIn Authentication'), don't render a second
        // app — hand the result to the opener and close this window.
        if ((successParam || errorParam) && window.opener && window.name === 'LinkedIn Authentication') {
            try {
                window.opener.postMessage(
                    { type: 'cc:linkedin-auth', ok: successParam === 'linkedin_auth_complete' },
                    window.location.origin
                );
            } catch (_) { }
            window.close();
            return;
        }

        if (successParam === 'linkedin_auth_complete') {
            setToast({ show: true, message: 'LinkedIn connected successfully!', type: 'success' });
            // Re-check connection status
            dispatch(checkLinkedInConnectionStatus());
            // Clean up URL
            window.history.replaceState({}, document.title, location.pathname);
        } else if (errorParam === 'linkedin_auth_failed') {
            setToast({ show: true, message: 'LinkedIn authentication failed. Please try again.', type: 'error' });
            // Clean up URL
            window.history.replaceState({}, document.title, location.pathname);
        } else if (errorParam === 'callback_error') {
            setToast({ show: true, message: 'An error occurred during authentication.', type: 'error' });
            // Clean up URL
            window.history.replaceState({}, document.title, location.pathname);
        }
    }, [location.search, dispatch]);

    const isConnected = isLinkedInConnected?.connected;
    // Until the connection check resolves, we don't know the real state — show a
    // skeleton rather than flashing the "not connected" hero on every refresh.
    const isCheckingConnection = !connectionChecked;

    // Inject this page's controls into the shared top navbar (LinkedIn status,
    // Back-to-pipeline, sort, search) instead of rendering a second header row.
    useNavbarActions(
        <>
            {isCheckingConnection ? (
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold bg-gray-50 text-gray-400 border border-gray-200">
                    <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" d="M12 2a10 10 0 0 1 10 10" /></svg>
                    Checking LinkedIn…
                </span>
            ) : isConnected ? (
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="#0A66C2"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" /></svg>
                    LinkedIn synced
                </span>
            ) : null}
            {isConnected && flowCompany && (
                <button
                    onClick={closeFlow}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E6E8EE] bg-white text-[12px] font-semibold text-[#334155] hover:bg-gray-50 transition-colors"
                >
                    <ArrowLeft style={{ width: 14, height: 14 }} /> Back to pipeline
                </button>
            )}
            {isConnected && !flowCompany && (
                <>
                    <select
                        value={sortBy}
                        onChange={e => setSortBy(e.target.value)}
                        className="px-3 py-1.5 text-[12px] border border-[#E6E8EE] rounded-lg bg-white text-[#334155] focus:outline-none focus:border-[#3F57E0]"
                    >
                        <option value="date">Sort: Newest</option>
                        <option value="alpha">Sort: A–Z</option>
                        <option value="progress">Sort: Progress</option>
                    </select>
                    <div className="relative">
                        <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" style={{ fontSize: 16 }} />
                        <input
                            type="text"
                            placeholder="Search companies…"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="pl-8 pr-3 py-1.5 text-[12px] border border-[#E6E8EE] rounded-lg bg-white focus:outline-none focus:border-[#3F57E0] w-44"
                        />
                    </div>
                </>
            )}
        </>,
        [isCheckingConnection, isConnected, flowCompany, sortBy, searchQuery]
    );

    return (
        <div className="relative h-screen bg-[#F6F7FB] pt-16 flex flex-col">
            <Navbaar />
            <div className="flex flex-1 min-h-0">
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </div>

                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                    {isSearching && (
                        <div className="px-6 py-2 bg-blue-50 border-b border-blue-100">
                            <p className="text-[12.5px] text-blue-700 font-medium animate-pulse">
                                🚀 Finding new contacts for you in real-time…
                            </p>
                        </div>
                    )}

                    {/* CHECKING CONNECTION — skeleton (avoids flashing the not-connected hero on refresh) */}
                    {isCheckingConnection ? (
                        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
                            <div className="space-y-2 animate-pulse">
                                <div className="h-5 bg-[#E9EDF3] rounded-full w-56" />
                                <div className="h-3 bg-[#F1F5F9] rounded-full w-80" />
                            </div>
                            <div className="flex items-center gap-2 animate-pulse">
                                {[64, 90, 96, 90].map((w, i) => (
                                    <div key={i} className="h-8 bg-[#F1F5F9] rounded-full" style={{ width: w }} />
                                ))}
                            </div>
                            <div className="space-y-3">
                                {[0, 1, 2, 3].map(i => <SkeletonRow key={i} />)}
                            </div>
                        </div>
                    ) : !isConnected ? (
                        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5" style={{ background: 'linear-gradient(160deg, #F8FAFF 0%, #EEF1FE 45%, #F5F3FF 100%)' }}>
                            {/* Hero connect card */}
                            <div className="rounded-2xl overflow-hidden border border-[#C7D0F8]">
                                <div className="grid grid-cols-2 relative overflow-hidden"
                                    style={{ background: 'linear-gradient(135deg, #1F2A56 0%, #3F57E0 60%, #6478F0 100%)', color: '#fff' }}>
                                    {/* Left */}
                                    <div className="p-8 relative z-10 text-left">
                                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/25 text-[11px] font-semibold tracking-wide uppercase mb-4">
                                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>
                                            Connect LinkedIn to start
                                        </div>
                                        <h2 className="text-[26px] font-extrabold text-white leading-tight tracking-tight">
                                            Unlock your<br />second-degree network
                                        </h2>
                                        <p className="text-[13.5px] text-white/85 mt-3 leading-relaxed max-w-md">
                                            Champ analyzes your 1st and 2nd-degree connections to surface the people most likely to refer you — recruiters, hiring managers, and peers at your target companies.
                                        </p>
                                        <div className="mt-6">
                                            <button
                                                onClick={handleLIConnect}
                                                disabled={liConnectLoading}
                                                className="inline-flex items-center gap-2.5 px-5 py-3 bg-white text-[#0A66C2] rounded-xl text-[14px] font-bold hover:bg-gray-50 transition-colors disabled:opacity-60"
                                                style={{ boxShadow: '0 8px 22px rgba(10,102,194,.25)' }}
                                            >
                                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>
                                                {liConnectLoading ? 'Connecting…' : 'Connect LinkedIn'}
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
                                            </button>
                                            <p className="text-[11.5px] text-white/70 mt-2.5">Takes 30 seconds · read-only · revoke anytime</p>
                                        </div>
                                        <div className="flex gap-5 mt-5 flex-wrap">
                                            {[
                                                { l: '32,400+', s: 'job seekers connected' },
                                                { l: '4.2×',    s: 'more responses with referrals' },
                                                { l: 'Read-only', s: 'never posts on your behalf' },
                                            ].map((s, i) => (
                                                <div key={i} className={`flex items-center gap-2 ${i > 0 ? 'pl-5 border-l border-white/20' : ''}`}>
                                                    <svg className="w-3 h-3 text-emerald-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                                                    <div>
                                                        <div className="text-[13px] font-bold text-white leading-none">{s.l}</div>
                                                        <div className="text-[10.5px] text-white/70 mt-0.5">{s.s}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {/* Right — network SVG */}
                                    <div className="flex items-center justify-center p-8">
                                        <svg viewBox="0 0 360 280" width="100%" style={{ maxWidth: 380 }}>
                                            <defs>
                                                <radialGradient id="n-glow" cx="50%" cy="50%" r="50%">
                                                    <stop offset="0%" stopColor="rgba(255,255,255,0.4)"/>
                                                    <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
                                                </radialGradient>
                                            </defs>
                                            <circle cx="180" cy="140" r="120" fill="url(#n-glow)" opacity=".5"/>
                                            {[[180,140,60,60],[180,140,300,70],[180,140,80,200],[180,140,290,220],[180,140,200,40],[180,140,110,140],[180,140,260,150],[180,140,170,250]].map(([x1,y1,x2,y2],i)=>(
                                                <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,255,255,.32)" strokeWidth="1" strokeDasharray="3 4"/>
                                            ))}
                                            <circle cx="180" cy="140" r="32" fill="#fff"/>
                                            <text x="180" y="146" textAnchor="middle" fontSize="14" fontWeight="800" fill="#1F2A56" fontFamily="Inter,sans-serif">YOU</text>
                                            {[{x:60,y:60,i:"MC",c:"#FBBF24"},{x:300,y:70,i:"DH",c:"#F472B6"},{x:80,y:200,i:"KP",c:"#60A5FA"},{x:290,y:220,i:"JR",c:"#34D399"}].map((p,i)=>(
                                                <g key={i}><circle cx={p.x} cy={p.y} r="20" fill={p.c}/><text x={p.x} y={p.y+4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#1F2A56" fontFamily="Inter,sans-serif">{p.i}</text></g>
                                            ))}
                                            {[{x:200,y:40},{x:110,y:140},{x:260,y:150},{x:170,y:250}].map((p,i)=>(
                                                <circle key={i} cx={p.x} cy={p.y} r="11" fill="rgba(255,255,255,.7)" stroke="rgba(255,255,255,.4)" strokeWidth="1"/>
                                            ))}
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            {/* Feature tiles */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {[
                                    {
                                        color: '#3F57E0',
                                        t: 'Warm intros, ranked',
                                        d: 'See your top connections at target companies, ordered by referral probability.',
                                        icon: (
                                            <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                                        ),
                                    },
                                    {
                                        color: '#7C3AED',
                                        t: 'AI-drafted outreach',
                                        d: 'Champ writes personalized intros referencing your shared connections and roles.',
                                        icon: (
                                            <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.6 4.6L18 9.2l-4.4 1.6L12 15l-1.6-4.2L6 9.2l4.4-1.6L12 3z" /><path d="M19 14l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" /><path d="M5 4l.5 1.5L7 6l-1.5.5L5 8l-.5-1.5L3 6l1.5-.5L5 4z" /></svg>
                                        ),
                                    },
                                    {
                                        color: '#F59E0B',
                                        t: 'Hiring signals from peers',
                                        d: "Know who's actively hiring or recently posted — surfaced as soon as they update.",
                                        icon: (
                                            <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="2" /><path d="M16.24 7.76a6 6 0 0 1 0 8.49M7.76 16.24a6 6 0 0 1 0-8.49M19.07 4.93a10 10 0 0 1 0 14.14M4.93 19.07a10 10 0 0 1 0-14.14" /></svg>
                                        ),
                                    },
                                    {
                                        color: '#10B981',
                                        t: 'Verified recruiter contacts',
                                        d: 'Get real recruiter emails and LinkedIn profiles for every company you target.',
                                        icon: (
                                            <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" /><path d="M22 7l-10 6L2 7" /></svg>
                                        ),
                                    },
                                    {
                                        color: '#E11D48',
                                        t: 'AI resume tailoring',
                                        d: 'Tailor your resume to each role and check your ATS score before you apply.',
                                        icon: (
                                            <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M16 13H8M16 17H8M10 9H8" /></svg>
                                        ),
                                    },
                                    {
                                        color: '#0EA5E9',
                                        t: 'Guided daily plan',
                                        d: 'A focused daily action list keeps your search on track and momentum high.',
                                        icon: (
                                            <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" /></svg>
                                        ),
                                    },
                                ].map((f, i) => (
                                    <div
                                        key={i}
                                        className="group rounded-2xl border p-5 flex flex-col items-center text-center transition-all hover:-translate-y-0.5 hover:shadow-md"
                                        style={{
                                            boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                                            background: ['Hiring signals from peers', 'AI resume tailoring', 'Guided daily plan'].includes(f.t)
                                                ? `radial-gradient(125% 95% at 100% 0%, ${f.color}2e 0%, ${f.color}0a 48%, #ffffff 100%)`
                                                : `linear-gradient(150deg, ${f.color}24 0%, ${f.color}0d 38%, #ffffff 100%)`,
                                            borderColor: `${f.color}2e`,
                                        }}
                                    >
                                        <div
                                            className="w-11 h-11 rounded-xl flex items-center justify-center mb-3.5 transition-transform group-hover:scale-105"
                                            style={{ background: `linear-gradient(135deg, ${f.color}22, ${f.color}0d)`, color: f.color, border: `1px solid ${f.color}26` }}
                                        >
                                            {f.icon}
                                        </div>
                                        <p className="text-[14px] font-extrabold text-[#0F172A] tracking-tight text-center">{f.t}</p>
                                        <p className="text-[12.5px] text-[#64748B] mt-1.5 leading-relaxed text-center">{f.d}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : flowCompany ? (
                    /* CONNECTED · SEQUENCE FLOW — full-page outreach sequence for one company */
                        <NetSequenceFlow key={flowCompany.organizationId || flowCompany.name} companyData={flowCompany} onBack={closeFlow} />
                    ) : (
                    /* CONNECTED · PIPELINE LIST — clickable company rows + pill filter chips */
                        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
                            {isPageLoading && networkingCompanies.length === 0 ? (
                                /* Initial load — skeleton rows instead of flashing the empty state */
                                <>
                                    <div className="space-y-2 animate-pulse">
                                        <div className="h-5 bg-[#E9EDF3] rounded-full w-56" />
                                        <div className="h-3 bg-[#F1F5F9] rounded-full w-80" />
                                    </div>
                                    <div className="flex items-center gap-2 animate-pulse">
                                        {[64, 90, 96, 90].map((w, i) => (
                                            <div key={i} className="h-8 bg-[#F1F5F9] rounded-full" style={{ width: w }} />
                                        ))}
                                    </div>
                                    {[0, 1, 2, 3].map(i => <SkeletonRow key={i} />)}
                                </>
                            ) : networkingCompanies.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 text-center">
                                    <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                                        <svg className="w-7 h-7 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                                    </div>
                                    <p className="text-[15px] font-bold text-[#0F172A]">No companies yet</p>
                                    <img className="mt-6 max-w-xs opacity-60" src={NetNotFoundBig} alt="" />
                                </div>
                            ) : (
                                <>
                                    {/* Heading */}
                                    <div>
                                        <div className="text-[18px] font-extrabold text-[#0F172A] tracking-tight text-left">Your networking pipeline</div>
                                        <div className="text-[12px] text-[#64748B] mt-0.5 text-left">
                                            Click a company to open its outreach sequence · {networkingCompanies.length} companies
                                        </div>
                                    </div>

                                    {/* Filter chips row — progress left, source right */}
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        {/* Progress chips */}
                                        <div className="flex items-center gap-2 flex-wrap">
                                        {[
                                            { k: 'all', l: 'All' },
                                            { k: 'in_progress', l: 'In progress' },
                                            { k: 'not_started', l: 'Not started' },
                                            { k: 'completed', l: 'Completed' },
                                        ].map((f) => {
                                            const isActive = activeFilter === f.k;
                                            return (
                                                <button
                                                    key={f.k}
                                                    onClick={() => setActiveFilter(f.k)}
                                                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[12.5px] font-semibold transition-colors ${
                                                        isActive
                                                            ? 'border border-[#3F57E0] bg-[#EEF1FE] text-[#3F57E0]'
                                                            : 'border border-[#E6E8EE] bg-white text-[#334155] hover:border-gray-300'
                                                    }`}
                                                >
                                                    {f.l}
                                                    <span
                                                        className={`text-[10px] font-bold px-1.5 py-px rounded-full ${
                                                            isActive ? 'bg-[#3F57E0]/15 text-[#3F57E0]' : 'bg-[#F1F3F7] text-[#94A3B8]'
                                                        }`}
                                                    >
                                                        {filterCounts[f.k]}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                        </div>

                                        {/* Source / save chips — right side */}
                                        <div className="flex items-center gap-2 flex-wrap">
                                        {[
                                            { k: 'job',     l: 'Jobs',          bg: '#EEF3FF', active: '#3F57E0', icon: '💼' },
                                            { k: 'signal',  l: 'Hiring Signal', bg: '#FFF7ED', active: '#D97706', icon: '⚡' },
                                            { k: 'saved',   l: 'Saved',         bg: '#F0FDF4', active: '#059669', icon: '🔖' },
                                            { k: 'unsaved', l: 'Unsaved',       bg: '#FFF1F2', active: '#E11D48', icon: '📭' },
                                        ].map((f) => {
                                            const isActive = sourceFilter === f.k;
                                            return (
                                                <button
                                                    key={f.k}
                                                    onClick={() => setSourceFilter(isActive ? null : f.k)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold transition-all border"
                                                    style={isActive
                                                        ? { background: f.bg, color: f.active, borderColor: f.active }
                                                        : { background: 'white', color: '#64748B', borderColor: '#E6E8EE' }
                                                    }
                                                >
                                                    <span>{f.icon}</span>
                                                    {f.l}
                                                </button>
                                            );
                                        })}
                                        </div>
                                    </div>

                                    {/* Company rows */}
                                    <div className="space-y-3">
                                        {isPageLoading ? (
                                            [0, 1, 2, 3].map(i => <SkeletonRow key={i} />)
                                        ) : pipelineCompanies.length === 0 ? (
                                            <div className="text-center text-[12.5px] text-[#94A3B8] py-12">
                                                No companies match this filter.
                                            </div>
                                        ) : (
                                            <>
                                                {visibleCompanies.map((company, index) => (
                                                    <NetPipelineRow
                                                        key={company.organizationId || index}
                                                        company={company}
                                                        onOpen={handleOpenFlow}
                                                        onDelete={handleDeleteCompany}
                                                    />
                                                ))}

                                                {/* Pagination footer */}
                                                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white rounded-2xl mt-1">
                                                    {/* Left: page-size dropdown */}
                                                    <div className="relative inline-flex items-center">
                                                        <select
                                                            value={pageSize}
                                                            onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                                                            className="appearance-none pl-3 pr-7 py-2 text-[12px] font-semibold text-[#3F57E0] bg-white border-2 border-[#3F57E0] rounded-lg shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#3F57E0]/20 hover:bg-[#EEF1FE] transition-colors"
                                                        >
                                                            <option value={10}>10 / page</option>
                                                            <option value={20}>20 / page</option>
                                                            <option value={50}>50 / page</option>
                                                            <option value={100}>100 / page</option>
                                                        </select>
                                                        <svg className="pointer-events-none absolute right-2 w-3.5 h-3.5 text-[#3F57E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </div>

                                                    {/* Right: count + page buttons */}
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-[12px] text-gray-500">
                                                            Showing{' '}
                                                            <span className="font-semibold text-gray-700">
                                                                {totalCompanies === 0 ? 0 : Math.min((currentPage - 1) * pageSize + 1, totalCompanies)}
                                                            </span>
                                                            {' '}–{' '}
                                                            <span className="font-semibold text-gray-700">
                                                                {Math.min(currentPage * pageSize, totalCompanies)}
                                                            </span>
                                                            {' '}of{' '}
                                                            <span className="font-semibold text-gray-700">{totalCompanies}</span>
                                                            {' '}companies
                                                        </span>

                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => goToPage(currentPage - 1)}
                                                                disabled={currentPage === 1}
                                                                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                                </svg>
                                                            </button>

                                                            {(() => {
                                                                const pages = [];
                                                                let start = Math.max(1, currentPage - 2);
                                                                let end   = Math.min(totalPages, start + 4);
                                                                if (end - start < 4) start = Math.max(1, end - 4);
                                                                for (let p = start; p <= end; p++) {
                                                                    pages.push(
                                                                        <button
                                                                            key={p}
                                                                            onClick={() => goToPage(p)}
                                                                            className={`min-w-[30px] h-[30px] px-2 rounded-md text-[12px] font-medium transition-colors ${
                                                                                p === currentPage
                                                                                    ? 'bg-[#3F57E0] text-white'
                                                                                    : 'text-gray-600 hover:bg-gray-100'
                                                                            }`}
                                                                        >
                                                                            {p}
                                                                        </button>
                                                                    );
                                                                }
                                                                return pages;
                                                            })()}

                                                            <button
                                                                onClick={() => goToPage(currentPage + 1)}
                                                                disabled={currentPage === totalPages}
                                                                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <SubscriptionModal isOpen={isSubscriptionModalOpen} onClose={() => setSubscriptionModalOpen(false)} />

            {toast.show && (
                <div className={`fixed bottom-5 left-1/2 -translate-x-1/2 px-5 py-2.5 rounded-xl text-white text-[13px] font-semibold shadow-lg z-50 ${toast.type === 'error' ? 'bg-red-500' : 'bg-emerald-600'}`}>
                    {toast.message}
                </div>
            )}
        </div>
    );
};

export default Networking;